export function generatePageContent(
  pascal: string,
  kebab: string,
  camel: string,
  label: string,
  actionImportPath: string,
  adminRoutePath = '/admin',
  devModeIntegrateButtonExportName: string,
  devModeIntegrateButtonImportPath: string,
  hasExport = false,
  hasDelete = true
): string {
  const exportIconImport = hasExport ? 'Download, ' : ''
  const exportHookImport = hasExport
    ? `\nimport { useExport${pascal}SubmissionsCSV } from '@admin/hooks/use-${kebab}-form'`
    : ''
  const exportHook = hasExport ? `\n  const exportCsv = useExport${pascal}SubmissionsCSV()` : ''
  const exportButton = hasExport
    ? `
            <Button
              variant="outline"
              onClick={() => exportCsv.mutate()}
              disabled={exportCsv.isPending}
            >
              <Download className="size-3.5" />
              Export CSV
            </Button>`
    : ''
  const queryClientImport = hasDelete ? ', useQueryClient' : ''
  const deleteActionImport = hasDelete
    ? `\nimport { deleteBulk${pascal}Submissions } from '@admin/actions/${actionImportPath}'`
    : ''
  const deleteDialogImport = hasDelete
    ? `\nimport { DeleteDialog } from '@admin/components/shared/delete-dialog'`
    : ''
  const toastImport = hasDelete ? `\nimport { toast } from 'sonner'` : ''
  const queryClient = hasDelete ? `  const queryClient = useQueryClient()\n` : ''
  const deleteTransition = hasDelete
    ? `  const [isPending, startTransition] = React.useTransition()\n`
    : ''
  const bulkDeleteHandler = hasDelete
    ? `
  const handleBulkDelete = () => {
    startTransition(async () => {
      try {
        const result = await deleteBulk${pascal}Submissions(selectedIds)

        if (result.success) {
          toast.success(
            \`\${selectedIds.length} submission\${selectedIds.length > 1 ? 's' : ''} deleted successfully\`
          )
          queryClient.refetchQueries({ queryKey: ['${camel}-submissions'] })
          void setSelectedIds([])
        } else {
          toast.error(result.error || 'Failed to delete submissions')
        }
      } catch (error) {
        toast.error('An error occurred')
        console.error(error)
      }
    })
  }
`
    : ''
  const bulkDeleteActions = hasDelete
    ? `
          actions={
            <div className="flex items-center gap-1">
              {selectedIds.length > 0 && (
                <DeleteDialog
                  count={selectedIds.length}
                  entityName="submission"
                  onConfirm={handleBulkDelete}
                  isPending={isPending}
                />
              )}
            </div>
          }`
    : ''
  return `'use client'

import type { Setting } from '@admin/actions/settings'
import { useIsFetching${queryClientImport} } from '@tanstack/react-query'
import { ${exportIconImport}Settings } from 'lucide-react'
import Link from 'next/link'
import { parseAsArrayOf, parseAsInteger, parseAsString, useQueryState, useQueryStates } from 'nuqs'
import * as React from 'react'
${toastImport}
${deleteActionImport}
${deleteDialogImport}
import { EntityFiltersBar } from '@admin/components/shared/entity-filters-bar'
import { PageHeader } from '@admin/components/shared/page-header'
import { Button } from '@admin/components/ui/button'
import { ${devModeIntegrateButtonExportName} } from '${devModeIntegrateButtonImportPath}'${exportHookImport}
import { columns } from './columns'
import { ${pascal}SubmissionDrawer } from './${kebab}-submission-drawer'
import { ${pascal}SubmissionsTable } from './${kebab}-submissions-table'

interface ${pascal}SubmissionsPageContentProps {
  initialSettings?: Setting | null
}

export function ${pascal}SubmissionsPageContent({
  initialSettings
}: ${pascal}SubmissionsPageContentProps) {
${queryClient}  const isSearchFetching = useIsFetching({ queryKey: ['${camel}-submissions'] }) > 0${exportHook}
  const [search, setSearch] = useQueryState('q', parseAsString.withDefault(''))
  const [, setPageIndex] = useQueryState('page', parseAsInteger.withDefault(0))
  const [selectedSubmissionId, setSelectedSubmissionId] = useQueryState(
    'submission',
    parseAsString.withDefault('')
  )
  const filterParsers = {
    submittedAtFrom: parseAsString.withDefault(''),
    submittedAtTo: parseAsString.withDefault(''),
  } as const
  const [filterValues, setFilters] = useQueryStates(filterParsers)
  const submittedAtFrom = filterValues.submittedAtFrom
  const submittedAtTo = filterValues.submittedAtTo
  const [selectedIds, setSelectedIds] = useQueryState(
    'selected',
    parseAsArrayOf(parseAsString).withDefault([])
  )
${deleteTransition}${bulkDeleteHandler}

  const activeFilterCount = [search, (submittedAtFrom || submittedAtTo)].filter(Boolean).length

  const handleViewSubmission = React.useCallback((id: string) => {
    void setSelectedSubmissionId(id)
  }, [setSelectedSubmissionId])

  const handleSubmissionDrawerOpenChange = React.useCallback((open: boolean) => {
    if (!open) {
      void setSelectedSubmissionId(null)
    }
  }, [setSelectedSubmissionId])

  const handleSearch = React.useCallback((value: string | null) => {
    React.startTransition(() => {
      void setPageIndex(0)
      void setSearch(value)
    })
  }, [setSearch, setPageIndex])

  const handleClearAll = React.useCallback(() => {
    React.startTransition(() => {
      void setPageIndex(0)
      void setSearch(null)
      void setFilters({
        submittedAtFrom: null,
        submittedAtTo: null
      })
    })
  }, [setSearch, setFilters, setPageIndex])

  const filters = [
    {
      key: 'submittedAt',
      label: 'Submitted',
      type: 'date-range' as const,
      from: submittedAtFrom,
      to: submittedAtTo
    }
  ]

  return (
    <React.Fragment>
      <PageHeader
        title="${label}"
        actions={
          <div className="flex items-center gap-1">
            <${devModeIntegrateButtonExportName} initialSettings={initialSettings} />${exportButton}
            <Button variant="outline" asChild>
              <Link href="${adminRoutePath}/forms/${kebab}/settings">
                <Settings className="size-3.5" />
                Settings
              </Link>
            </Button>
          </div>
        }
      />
      <EntityFiltersBar
          filters={filters}
          activeFilterCount={activeFilterCount}
          onFilterChange={(updates) => {
            React.startTransition(() => {
              void setPageIndex(0)
              void setFilters(updates)
            })
          }}
          onClearAll={handleClearAll}
          search={search}
          searchPlaceholder="Search ${label.toLowerCase()} submissions..."
          isPending={isSearchFetching}
          onSearch={handleSearch}
${bulkDeleteActions}
        />
      <main className="space-y-4 px-4 pb-4 flex-1">
        <${pascal}SubmissionsTable
          columns={columns}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onViewSubmission={handleViewSubmission}
          search={search}
          submittedAtFrom={submittedAtFrom}
          submittedAtTo={submittedAtTo}
        />
      </main>
      <${pascal}SubmissionDrawer
        open={Boolean(selectedSubmissionId)}
        submissionId={selectedSubmissionId || null}
        onOpenChange={handleSubmissionDrawerOpenChange}
      />
    </React.Fragment>
  )
}
`
}
